import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Pencil, Plus, Trash2, Save, X, GripVertical } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useEditMode } from "@/contexts/EditModeContext";

interface TestimonialVideo {
  video_url: string;
  thumbnail_url: string;
  author: string;
  company: string;
  quote: string;
}

interface EditableTestimonialVideosProps {
  videos: TestimonialVideo[];
  onSave: (videos: TestimonialVideo[]) => Promise<void>;
}

export const EditableTestimonialVideos = ({ videos, onSave }: EditableTestimonialVideosProps) => {
  const { isEditor } = useAuth();
  const { isEditMode } = useEditMode();
  const [isEditing, setIsEditing] = useState(false);
  const [editedVideos, setEditedVideos] = useState<TestimonialVideo[]>(videos);
  const [isSaving, setIsSaving] = useState(false);

  if (!isEditor || !isEditMode) {
    return null;
  }

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(editedVideos.filter(v => v.video_url));
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedVideos(videos);
    setIsEditing(false);
  };

  const handleAdd = () => {
    setEditedVideos([...editedVideos, {
      video_url: '',
      thumbnail_url: '',
      author: '',
      company: '',
      quote: ''
    }]);
  };

  const handleRemove = (index: number) => {
    setEditedVideos(editedVideos.filter((_, i) => i !== index));
  };

  const handleUpdate = (index: number, field: keyof TestimonialVideo, value: string) => {
    const updated = [...editedVideos];
    updated[index] = { ...updated[index], [field]: value };
    setEditedVideos(updated);
  };

  if (isEditing) {
    return (
      <Card className="p-6 border-dashed border-2 border-primary/50 bg-primary/5">
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Edit Testimonial Videos</h3>
            <div className="flex gap-2">
              <Button onClick={handleAdd} size="sm" variant="outline">
                <Plus className="w-4 h-4 mr-1" /> Add Video
              </Button>
              <Button onClick={handleSave} size="sm" disabled={isSaving}>
                <Save className="w-4 h-4 mr-1" /> Save
              </Button>
              <Button onClick={handleCancel} size="sm" variant="outline">
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
            </div>
          </div>

          {editedVideos.map((video, index) => (
            <Card key={index} className="p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <GripVertical className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Video {index + 1}</span>
                </div>
                <Button
                  onClick={() => handleRemove(index)}
                  size="sm"
                  variant="ghost"
                  className="text-destructive"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <Input
                placeholder="Video URL"
                value={video.video_url}
                onChange={(e) => handleUpdate(index, 'video_url', e.target.value)}
              />
              <Input
                placeholder="Thumbnail URL"
                value={video.thumbnail_url}
                onChange={(e) => handleUpdate(index, 'thumbnail_url', e.target.value)}
              />
              <Input
                placeholder="Author Name"
                value={video.author}
                onChange={(e) => handleUpdate(index, 'author', e.target.value)}
              />
              <Input
                placeholder="Company"
                value={video.company}
                onChange={(e) => handleUpdate(index, 'company', e.target.value)}
              />
              <Textarea
                placeholder="Quote"
                value={video.quote}
                onChange={(e) => handleUpdate(index, 'quote', e.target.value)}
                rows={3}
              />
            </Card>
          ))}
        </div>
      </Card>
    );
  }

  return (
    <div className="relative group">
      <Button
        onClick={() => setIsEditing(true)}
        size="sm"
        variant="outline"
        className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity z-10"
      >
        <Pencil className="w-4 h-4 mr-1" /> Edit Videos
      </Button>
    </div>
  );
};
